import React, { useState, useMemo } from 'react';
import { Modal, Button, Table, Row, Col, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt } from '@fortawesome/free-solid-svg-icons';
import { API_URL } from '../services/api';
import { toast } from 'react-hot-toast';
import PDFViewerModal from './PDFViewerModal';
import DocumentViewerModal from './DocumentViewerModal';

function WRDetailsModal({ show, onHide, wrDetails }) {
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [documentViewerState, setDocumentViewerState] = useState({
    show: false,
    url: '',
    title: ''
  });
  
  // Get document server URL from environment variable
  const docsrvUrl = import.meta.env.VITE_DOCSRV || '';

  // Calculate grand total for rates
  const grandTotal = useMemo(() => {
    if (!wrDetails?.rates || wrDetails.rates.length === 0) return 0;
    
    return wrDetails.rates.reduce((sum, rate) => {
      const qty = parseFloat(rate.Qty) || 0;
      const rateValue = parseFloat(rate.Rate) || 0;
      const total = qty * rateValue;
      return sum + total;
    }, 0);
  }, [wrDetails?.rates]);

  // Format date strings to local format
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Create document URL with the DOCSRV environment variable
  const getDocumentUrl = (filename) => {
    if (!filename) return '#';
    
    // Check if the filename already has a protocol/domain
    if (filename.startsWith('http://') || filename.startsWith('https://')) {
      return filename;
    }
    
    // Remove any leading slashes from the filename
    const cleanFilename = filename.replace(/^\/+/, '');
    
    // Ensure docsrvUrl has a trailing slash
    const baseUrl = docsrvUrl.endsWith('/') ? docsrvUrl : `${docsrvUrl}/`;
    
    return `${baseUrl}${cleanFilename}`;
  };

  // Handle generating and opening warehouse receipt
  const handlePrintReceipt = async () => {
    if (!wrDetails) return;
    
    try {
      // Show loading state
      toast.loading("Generating warehouse receipt...");
      
      // Call API to generate warehouse receipt PDF
      const response = await fetch(`${API_URL}/api/warehouse/receipts/${wrDetails.idwarehouse}/receipt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Get content type from response
      const contentType = response.headers.get('content-type');
      console.log("WR Receipt PDF content type:", contentType);
      
      // Create a blob URL for the PDF
      const responseBlob = await response.blob();
      const blob = new Blob([responseBlob], { type: contentType });
      const blobUrl = URL.createObjectURL(blob);
      
      // Prepare document data for the viewer
      const receiptDocument = {
        descr: `Warehouse Receipt ${wrDetails.idwarehouse || wrDetails.bookingNum || ''}`,
        fileName: `WR_Receipt_${wrDetails.idwarehouse || wrDetails.bookingNum || 'Unknown'}.pdf`,
        blobUrl: blobUrl,
        type: contentType || 'application/pdf',
        isReceipt: true
      };
      
      // Close loading toast
      toast.dismiss();
      
      // Show success message
      toast.success("Warehouse receipt generated successfully");
      
      // Show the document in the viewer modal
      setDocumentViewerState({
        show: true,
        url: receiptDocument.blobUrl,
        title: receiptDocument.descr,
        document: receiptDocument
      });
    } catch (error) {
      console.error("Failed to generate warehouse receipt:", error);
      toast.dismiss();
      toast.error(`Failed to generate warehouse receipt: ${error.message || 'Unknown error'}`);
    }
  };

  // Handle opening a document in the modal
  const handleDocumentClick = (doc, e) => {
    e.preventDefault();
    
    // Check if this is the receipt document
    if (doc.isReceipt) {
      handlePrintReceipt();
      return;
    }
    
    // Prepare document data for the viewer
    const documentData = {
      ...doc,
      descr: doc.Description || 'Document',
      fileName: doc.filename,
      blobUrl: getDocumentUrl(doc.filename)
    };
    
    setDocumentViewerState({
      show: true,
      url: documentData.blobUrl,
      title: documentData.descr,
      document: documentData
    });
  };

  // Handle closing the document viewer
  const handleCloseDocumentViewer = () => {
    setDocumentViewerState({
      ...documentViewerState,
      show: false
    });
  };

  // Add this function for dynamic card heights
  const getCardStyle = (contentType, rows) => {
    // If content is empty, make it very compact
    if (!rows || rows.length === 0) {
      return { 
        maxHeight: 'none',
        minHeight: 'auto',
        height: 'auto'
      };
    }
    
    // For small number of rows, make it compact with no extra space
    if (rows.length <= 3) {
      return { 
        maxHeight: 'none',
        minHeight: 'auto',
        height: 'auto'
      };
    }
    
    // For medium number of rows (4-6), allow a bit more height but still compact
    if (rows.length <= 6) {
      return { 
        maxHeight: '180px',
        overflowY: 'auto',
        minHeight: 'auto'
      };
    }
    
    // For many rows, limit height and add scroll
    return { 
      maxHeight: '200px', 
      overflowY: 'auto',
      minHeight: 'auto'
    };
  };

  if (!wrDetails) {
    return null;
  }

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" backdrop="static" centered>
        <Modal.Header closeButton className="py-2">
          <Modal.Title>WR Details</Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-2">
          <Row className="mb-2">
            <Col md={3}>
              <div className="mb-1">
                <label className="fw-bold d-block">Booking Number</label>
                <div>{wrDetails.bookingNum || '-'}</div>
              </div>
              <div className="mb-1">
                <label className="fw-bold d-block">Shipper</label>
                <div>{wrDetails.shipper || '-'}</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="mb-1">
                <label className="fw-bold d-block">Date of Receipt</label>
                <div>{formatDate(wrDetails.recDate)}</div>
              </div>
              <div className="mb-1">
                <label className="fw-bold d-block">Date of Entry</label>
                <div>{formatDate(wrDetails.entryDate)}</div>
              </div>
            </Col>
            <Col md={6}>
              <div>
                <label className="fw-bold d-block">Notes</label>
                <div className="border p-1 bg-light" style={{ minHeight: '80px' }}>
                  {wrDetails.notes || '-'}
                </div>
              </div>
            </Col>
          </Row>
          
          <Row className="mb-2" style={{ marginTop: '0.25rem' }}>
            <Col md={12}>
              <div className="mb-1">
                <label className="fw-bold d-block">Consignee</label>
                <div className="row g-0">
                  <div className="col-4">{wrDetails.consignee || '-'}</div>
                  <div className="col-3">
                    <span className="fw-bold me-1">Dest:</span>
                    {wrDetails.destination || '-'}
                  </div>
                  <div className="col-3">
                    <span className="fw-bold me-1">Carrier:</span>
                    {wrDetails.carrier || '-'}
                  </div>
                  <div className="col-2">
                    <span className="fw-bold me-1">Pro:</span>
                    {wrDetails.proNum || '-'}
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          <Card className="shadow-sm freight-card" style={{ marginBottom: '2rem !important' }}>
            <Card.Header className="py-2">
              <h6 className="mb-0">Freight</h6>
            </Card.Header>
            <Card.Body className="p-0" style={getCardStyle('freight', wrDetails.freight)}>
              <div className="table-responsive" style={{ margin: 0, padding: 0 }}>
                <Table size="sm" className="m-0">
                  <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                    <tr>
                      <th>FID</th>
                      <th>Customer Ref</th>
                      <th>MAWB</th>
                      <th>HAWB</th>
                      <th>Notes</th>
                      <th>Pcs</th>
                      <th>Wt</th>
                      <th>POD Name</th>
                      <th>POD Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wrDetails.freight && wrDetails.freight.length > 0 ? (
                      wrDetails.freight.map((item) => (
                        <tr key={item.FID}>
                          <td>{item.FID}</td>
                          <td>{item.CustomerRef || '-'}</td>
                          <td>{item.MAWB || '-'}</td>
                          <td>{item.HAWB || '-'}</td>
                          <td>{item.Notes || '-'}</td>
                          <td>{item.Pcs}</td>
                          <td>{item.Wt}</td>
                          <td>{item.PODName || '-'}</td>
                          <td>{formatDate(item.PODDate)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="9" className="text-center py-2">No freight found</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>

          <Row className="g-2 equal-height-row">
            <Col md={6} className="d-flex">
              <Card className="shadow-sm flex-fill">
                <Card.Header className="py-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Rates</h6>
                    <div className="text-end">
                      <span className="fw-bold">Grand Total: </span>
                      <span className="text-success fw-bold">${grandTotal.toFixed(2)}</span>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body className="p-0 d-flex flex-column" style={getCardStyle('rates', wrDetails.rates)}>
                  <div className="table-responsive flex-fill" style={{ margin: 0, padding: 0 }}>
                    <Table size="sm" className="m-0">
                      <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                        <tr>
                          <th>Description</th>
                          <th>Qty</th>
                          <th>Rate</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {wrDetails.rates && wrDetails.rates.length > 0 ? (
                          wrDetails.rates.map((rate, index) => (
                            <tr key={index}>
                              <td>{rate.Description}</td>
                              <td>{rate.Qty}</td>
                              <td>${parseFloat(rate.Rate).toFixed(2)}</td>
                              <td>${parseFloat(rate.Total).toFixed(2)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="4" className="text-center py-2">No rates found</td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            
            <Col md={6} className="d-flex">
              <Card className="shadow-sm flex-fill">
                <Card.Header className="py-2">
                  <h6 className="mb-0">Documents</h6>
                </Card.Header>
                <Card.Body className="p-0 d-flex flex-column" style={getCardStyle('documents', wrDetails.documents)}>
                  <div className="table-responsive flex-fill" style={{ margin: 0, padding: 0 }}>
                    <Table size="sm" className="m-0">
                      <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                        <tr>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Add Warehouse Receipt document row at the top */}
                        <tr 
                          onClick={(e) => handleDocumentClick({ isReceipt: true }, e)}
                          style={{ cursor: 'pointer' }}
                          className="table-row-hover"
                        >
                          <td>
                            <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                            Warehouse Receipt for {wrDetails.bookingNum || wrDetails.idwarehouse || 'WR'}
                          </td>
                        </tr>
                        {/* Regular document rows */}
                        {wrDetails.documents && wrDetails.documents.length > 0 ? (
                          wrDetails.documents.map((doc) => (
                            <tr 
                              key={doc.id}
                              onClick={(e) => handleDocumentClick(doc, e)}
                              style={{ cursor: 'pointer' }}
                              className="table-row-hover"
                            >
                              <td>
                                <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                                {doc.Description}
                              </td>
                            </tr>
                          ))
                        ) : (
                          wrDetails.documents.length === 0 && (
                            <tr>
                              <td className="text-center py-2">No additional documents found</td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="py-1">
          <Button variant="secondary" onClick={onHide}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* PDF Viewer Modal for WR */}
      <PDFViewerModal 
        show={showPdfViewer} 
        onHide={() => setShowPdfViewer(false)} 
        wrDetails={wrDetails} 
      />

      {/* Document Viewer Modal for external documents */}
      <DocumentViewerModal
        show={documentViewerState.show}
        onHide={handleCloseDocumentViewer}
        document={documentViewerState.document}
      />

      <style>{`
        .modal-content {
          max-height: 90vh;
        }
        
        .modal-body {
          max-height: calc(90vh - 120px);
          overflow-y: auto;
          padding: 0.5rem !important;
        }

        .card {
          margin-bottom: 0 !important;
        }

        .card-header {
          background-color: #f8f9fa;
          border-bottom: 1px solid rgba(0,0,0,.125);
          padding: 0.5rem 0.75rem !important;
        }

        .card-header h6 {
          font-size: 0.875rem;
          margin: 0;
        }

        .card-body {
          background-color: white;
          padding: 0 !important;
        }

        .table thead th {
          position: sticky;
          top: 0;
          background: white;
          z-index: 1;
          font-size: 0.875rem;
          padding: 0.25rem 0.5rem;
          border-bottom: 1px solid #dee2e6;
        }

        .table td {
          font-size: 0.875rem;
          padding: 0.25rem 0.5rem;
          vertical-align: middle;
          border-top: 1px solid #dee2e6;
        }

        .table {
          margin-bottom: 0 !important;
        }

        .table-sm thead th,
        .table-sm td {
          padding: 0.25rem 0.5rem;
        }

        /* Equal height cards using flexbox */
        .equal-height-row {
          display: flex;
          flex-wrap: wrap;
        }

        .equal-height-row .col-md-6 {
          display: flex !important;
        }

        .equal-height-row .card {
          display: flex;
          flex-direction: column;
          width: 100%;
        }

        .equal-height-row .card-body {
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .equal-height-row .table-responsive {
          flex: 1;
        }

        /* Override card body padding for equal height cards */
        .equal-height-row .card-body {
          padding: 0 !important;
          min-height: auto !important;
          height: auto !important;
        }

        /* Clickable table rows */
        .table-row-hover:hover {
          background-color: #f8f9fa !important;
        }

        /* Freight card spacing */
        .freight-card {
          margin-bottom: 2rem !important;
        }

        /* Make cards more compact */
        .modal .card-body {
          min-height: auto !important;
          height: auto !important;
        }

        /* Compact table styling */
        .modal .table-sm {
          font-size: 0.875rem;
        }

        .modal .table-sm td,
        .modal .table-sm th {
          padding: 0.25rem 0.5rem !important;
          vertical-align: middle;
        }

        /* Remove extra spacing from table containers */
        .modal .table-responsive {
          margin: 0 !important;
          padding: 0 !important;
          min-height: auto !important;
          height: auto !important;
        }

        /* Force compact layout */
        .modal .table {
          margin-bottom: 0 !important;
          height: auto !important;
          min-height: auto !important;
        }

        /* Custom scrollbar styling */
        .modal-body::-webkit-scrollbar,
        div[style*="overflow-y: auto"]::-webkit-scrollbar {
          width: 6px;
        }

        .modal-body::-webkit-scrollbar-track,
        div[style*="overflow-y: auto"]::-webkit-scrollbar-track {
          background: #f1f1f1;
        }

        .modal-body::-webkit-scrollbar-thumb,
        div[style*="overflow-y: auto"]::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 3px;
        }

        .modal-body::-webkit-scrollbar-thumb:hover,
        div[style*="overflow-y: auto"]::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
      `}</style>
    </>
  );
}

export default WRDetailsModal;